/**
 * AssignmentPanel — 배포본 상세·채점·마감(계약 §2 assignments · §5-2).
 *
 * 채점은 화면에서 먼저 쌓고 [채점 저장]에서 바뀐 문항만 PATCH 한다. 한 문항 누를 때마다 보내면
 * 20문항 채점이 20번의 실패 지점이 되고, 실패했을 때 어디까지 갔는지도 화면에 남지 않는다.
 * 계약이 부분 채점을 허용하므로(§2 · §5-2) 전 문항을 강요하지 않는다 — 채점한 것만 리포트에 간다.
 *
 * 응답 형태는 contract-answers §2-9 로 확정됐다: 문항 배열 = items, 채점값 = JSON true|false|null,
 * 마감 = is_closed, 상단 이름 = student_name·paper_title, PATCH 응답 = 갱신된 상세 전체.
 *
 * 2026-08-26 계약 §1-2(A1·A2·A3)로 이 패널의 세 동작이 전부 바뀌었다.
 *  · 채점해도 status 가 200 으로 넘어가지 않는다 — **마감 판정은 is_closed 또는 status===200 뿐**이다.
 *    (예전에는 다 채점하면 200 이 돼 화면이 저절로 "마감"으로 읽혔다. 옛 데이터는 마이그레이션으로 보정됐다.)
 *  · 예약(open_at 미도래 · status 50)인 배정은 채점·마감이 **409 reason:'scheduled'** 로 막힌다 → 화면도 잠근다.
 *  · 재오픈은 close_at(자동 마감 예약)까지 풀고 status 를 되돌린다(오픈 전이면 50, 지났으면 100).
 *    이전에는 예약이 남아 60초 안에 다시 마감됐다. **마감이 아닌 배정에 부르면 423.**
 *  · 재오픈 중에는 이 배정이 라이브 리포트에서 빠져 있고 다시 [마감]해야 재편입된다 —
 *    이미 저장·공유된 KY Report 스냅샷은 불변이다(contract-answers §3-3).
 *
 * 실패 문구는 셋 다 describeApiError 로 낸다. 서버 message 를 그대로 띄우면 사유(data.reason)가
 * 화면에서 사라져, 같은 409 라도 "예약이라 막힘"과 "다른 곳에서 먼저 수정함"을 구분할 수 없다.
 */
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Save, Undo2, Lock, Unlock, AlertTriangle, RefreshCw, CircleCheckBig, CircleX, ClipboardList, CalendarClock, Trash2, Printer } from 'lucide-react';
import { InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css'; // 쓰는 파일이 각자 넣는 것이 이 저장소 관례다(29곳) — 빠지면 수식이 조용히 깨진다
import { Button } from '../common';
import { useAuth } from '../../context/AuthContext';
/**
 * 배정 삭제를 누가 할 수 있는가 — 정본은 roles.js 한 곳이다(`userType === 'manager'` 를 여기 심으면
 * 그 파일이 정본이 아니게 된다).
 *
 * **판정을 prop 으로 받지 않고 이 부품이 직접 읽는다 — 이 저장소에서 여기만 그렇게 한다.**
 * 문제 v2 쪽은 부모가 판정해 내려보내지만, 이 패널은 언제나 AssignmentGradeModal 을 거쳐
 * 마운트되고 **그 창을 띄우는 부모가 셋**이다(시험지 화면 · 학습 화면의 테스트 목록 탭 · 배정 피드).
 * prop 으로 만들면 껍데기 1 + 부모 3 = 네 자리가 같은 판정을 각자 적어야 하고, 넷 중 하나가
 * 빠지는 날 그 입구로 들어온 교사에게만 삭제 버튼이 남는다. 이 패널은 이미 `useAuth()` 로 토큰을
 * 받고 있어 유형을 하나 더 꺼내는 것이 배선 한 줄이다.
 */
import { ASSIGN_DELETE_ROLES } from '../../utils/roles';
import { parseV2Timestamp } from '../../utils/v2DateTime';
// 문제 번호 — 두 메뉴가 함께 쓰는 정본 부품(문제 v2 목록 표가 기준 얼굴이다)
import ProblemIdLink from '../ProblemHubV2/ProblemIdLink';
/**
 * 본문 칸 — 문제 v2 목록 표(ProblemHubV2/ProblemTable)와 **같은 부품·같은 얼굴**이다
 * (오너 지시 2026-09-06 「문제 v2의 테이블을 참고해」). 종전에는 평문 `preview` 를 두 줄로
 * 잘라 놓아 수식이 `\dfrac{1}{2}` 같은 LaTeX 원문으로 보였고, 그 행이 무슨 문제인지
 * 알아볼 수 없었다 — 문제 v2 가 2026-08-29 에 같은 이유로 넘어온 자리다.
 *
 * `compact fill` 을 쓰는 것도 그쪽과 같다: 표에서는 행 높이를 옆 칸이 정하므로 본문이
 * 그 높이를 받아 채우기만 한다(못을 박으면 칸 아래가 빈다). 배율 상한도 같은 상수를 쓴다.
 */
import ProblemBodyPreview, { LIST_BODY_MAX_SCALE } from './ProblemBodyPreview';
/**
 * 본문을 **눈에 든 행만** 받아 오는 정본 훅. 문제 v2 표와 갈리는 유일한 지점이 여기다 —
 * 그쪽은 검색 응답이 본문을 함께 실어 오지만(with_body=1, 계약 §9), 배정 상세에는 본문이
 * 없어 문제 번호로 따로 물어야 한다. 38문항짜리를 열자마자 조판 본문 38벌을 한꺼번에
 * 끌어오지 않도록, 화면에 든 행만 20개씩 받는다(관찰 대상은 `[data-body-pid]`).
 */
import useRowBodies from './useRowBodies';
/**
 * 정답을 화면에 어떻게 그릴지는 이 저장소에 이미 정본이 있다 — 원문자 ①~⑳ 은 평문(KaTeX 에
 * 글리프 메트릭이 없다) · 나머지는 숫자를 포함해 수식. 여기서 규칙을 새로 만들면 같은 정답이
 * 자리마다 다른 얼굴이 된다. OcrHub 소속이지만 의존성 없는 순수 함수다.
 */
import parseAnswerForDisplay from '../OcrHub/answerLatex';
import {
  fetchAssignment, patchResults, closeAssignment, reopenAssignment, deleteAssignment, resultDisplay,
  assignStatusDisplay, ASSIGN_STATUS, fmtServerDateTime, describeApiError, errorReason,
  AUTH_REDIRECT } from './examPaperApi';

const GRADE_OPTIONS = [[true, 'O'], [false, 'X'], [null, '미채점']];

/** 채점값은 JSON 3-state 고정(§2-9) — true/false 가 아니면 전부 미채점(null=기록만 있음 포함) */
const toResult = (v) => (v === true ? true : (v === false ? false : null));

/** 배정 상세는 형태가 확정됐어도 값은 비어 올 수 있다 — 배열이 아니면 빈 목록으로 다룬다 */
function normalizeItems(a) {
  const raw = Array.isArray(a?.items) ? a.items : [];
  return raw
    .map((it, i) => {
      /**
       * 이 문항이 시험지에서 몇 번째인가. **`items[].order_no` 는 0부터**가 계약이다
       * (examPaperApi listPapers 주석: `matched_order_no` 가 「상세 items[].order_no 와 같은 기준」).
       * 값이 없으면 응답 순서를 그 자리에 놓는다.
       *
       * 한 번만 셈해 둘로 나눈다 — 정렬은 서버 자로, 사람에게 보일 번호는 +1 로. 같은 식을 두
       * 벌 적으면 한쪽만 고쳐도 빌드는 통과하고, 번호와 순서가 조용히 갈린다.
       */
      const seq = typeof it?.order_no === 'number' ? it.order_no : i;
      return {
      // 채점 상태를 담는 키 — problem_id 가 비어 온 행까지 한 규칙으로 다뤄 값이 엇갈리지 않게 한다
      key: String(it?.problem_id ?? `row-${i}`),
      problemId: it?.problem_id ?? null,
      orderNo: seq,
      /**
       * 사람이 세는 번호(문제 v2 의 paperUsageApi.matchedLabelOfRow 와 같은 자).
       * 종전에는 서버 값을 그대로 그려 **첫 문항이 0번**이었다(오너 지적 2026-09-06) — 38문항짜리가
       * 0~37 로 서서 종이의 1~38 과 하나씩 어긋났고, 읽어 주는 이름(「0번 문항 채점」)도 함께 밀렸다.
       */
      displayNo: seq + 1,
      preview: it?.preview ?? '',
      points: it?.points ?? null,
      result: toResult(it?.result),
      /**
       * 정답과 그 출처(2026-09-06 회신 §2). 은행에 정답이 없거나 더미('.')면 서버가 null 을 준다 —
       * 빈 문자열로 눌러 두지 않는다. 「정답이 없는 문제」와 「값을 못 받은 상태」가 같은 얼굴이면
       * 채점자가 화면을 믿지 못한다.
       */
      answer: it?.answer ?? null,
      answerSource: it?.answer_source ?? null,
      };
    })
    // 이 순서가 곧 출제 순서다. sort 는 안정적이라 order_no 가 비어 온 행은 응답 순서를 지킨다
    .sort((x, y) => x.orderNo - y.orderNo);
}

/**
 * 마감 판정은 **is_closed 또는 status===200 뿐**이다(계약 §1-2 A2). 채점을 다 해도 status 는 50/100 에 머문다.
 * closed_at 을 섞지 않는 이유: 재오픈은 status·close_at 을 되돌리지만 "언제 마감했었나"인 closed_at 은
 * 남아 올 여지가 있어, 그것까지 근거로 삼으면 다시 열린 배정이 화면에서 영영 마감으로 굳는다.
 * 계약이 보장하는 두 키가 **둘 다 없을 때만** 최후 근거로 본다.
 */
function isClosedOf(a) {
  if (a?.is_closed === true || a?.status === 200) return true;
  if (a?.is_closed == null && a?.status == null) return a?.closed_at != null;
  return false;
}

/**
 * 예약(오픈 전) 판정 — 정본은 서버 status 50 이다(계약 §1-2). 예약 배정은 채점·마감이 409 로 막힌다.
 * status 가 비어 올 때만 open_at 을 이 브라우저 시계와 견준다 — 시계가 틀어져 있으면 어긋나므로 보조 근거다.
 * open_at 은 UTC 시각이다(계약 §1-1). 존 없는 옛 응답도 공통 파서로 읽는다.
 */
function isScheduledOf(a) {
  if (a?.status === 50) return true;
  if (a?.status != null) return false;
  const t = parseV2Timestamp(a?.open_at)?.getTime();
  return t != null && t > Date.now();
}

/**
 * 오픈 시각이 이미 지났는가. 마감 상태(status 200)에서는 isScheduledOf 가 늘 false 라
 * "재오픈하면 예약으로 돌아간다"(§1-2 A1)를 그것으로는 알 수 없어 시각만 따로 본다.
 * 값이 없거나 못 읽으면 '지났다'로 본다 — 확실하지 않은 경고를 띄우지 않기 위해서다.
 */
function openAtPassedOf(a) {
  const t = parseV2Timestamp(a?.open_at)?.getTime();
  return t == null || t <= Date.now();
}

/** 3-state 채점 컨트롤 — 배타 선택이라 라디오그룹. 화살표로 옮기고 그 자리에서 선택된다 */
function GradeControl({ label, value, onChange, disabled }) {
  const btnRefs = useRef([]);
  const idx = Math.max(0, GRADE_OPTIONS.findIndex(([v]) => v === value));

  const move = (delta) => {
    if (disabled) return;
    const next = (idx + delta + GRADE_OPTIONS.length) % GRADE_OPTIONS.length;
    onChange(GRADE_OPTIONS[next][0]);
    btnRefs.current[next]?.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); move(1); }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
  };

  return (
    <div role="radiogroup" aria-label={label} onKeyDown={onKeyDown} className="flex shrink-0 items-center gap-1">
      {GRADE_OPTIONS.map(([v, text], i) => {
        const on = value === v;
        const tone = v === true
          ? 'border-green-500 bg-green-500 text-white'
          : (v === false ? 'border-red-500 bg-red-500 text-white' : 'border-gray-400 bg-gray-400 text-white');
        return (
          <button
            key={text}
            type="button"
            ref={(el) => { btnRefs.current[i] = el; }}
            role="radio"
            aria-checked={on}
            tabIndex={i === idx ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(v)}
            className={`rounded-full border px-2 py-1 text-xs font-bold transition-colors disabled:opacity-40 ${
              on ? tone : 'border-gray-200 bg-white text-gray-500 hover:border-primary/50'
            }`}
          >
            {text}
          </button>
        );
      })}
    </div>
  );
}

/**
 * @param {boolean} embedded 모달 안에서 쓰는가. 모달은 자기 [닫기](X)를 갖고 미저장 확인도 자기가
 *   하므로, 여기의 [뒤로]를 함께 그리면 같은 일을 하는 버튼이 둘이 되고 확인창도 두 번 뜬다.
 *   그래서 embedded 면 [뒤로]만 내린다 — 나머지(제목·상태·새로고침·채점·마감)는 그대로다.
 */
/**
 * 정답 칸 — 2026-09-06 회신 §2 의 `answer` 를 그린다.
 *
 * **값이 없으면 빈 칸으로 두지 않고 「정답 정보 없음」을 적는다**(오너 결정 2026-09-06 D2).
 * 빈 칸이면 「정답이 없는 문제」와 「값을 못 받은 상태」가 같은 모양이 되어, 채점자가 화면을
 * 믿지 못한 채 종이 정답지를 다시 꺼내게 된다.
 *
 * ⛔ **정답 출처 꼬리표를 붙이지 않는다**(오너 확인 2026-09-06 — 「현재 정답과 동일한데도 배지가
 * 표시된다」).
 *
 * 문구를 세 번 고쳤다: 「현재 값」 → 「은행 정답」 → 「출제 당시와 다름」. 셋 다 어긋났고, 원인은
 * 표현이 아니라 **말할 내용 자체에 있었다.** 채점자가 알고 싶은 것은 「이 정답이 출제 때와
 * 달라졌는가」 하나인데, 지금 데이터로는 그것을 판정할 수 없다:
 *   answer_source:'snapshot' 출제 시점 값이 저장돼 있다. 현재 값과 견주면 알 수 있으나 그 비교는
 *                            **서버만** 할 수 있다 — 화면은 지금 문제의 정답을 따로 받지 않는다.
 *   answer_source:'current'  스냅샷이 없어(회신 §4-1, 소급 불가) 견줄 대상 자체가 없다.
 * 그래서 'current' 는 「달라졌다」가 아니라 「모른다」는 뜻이고, 실제로는 문제를 고치지 않은 문항이
 * 대부분이다. 그 문항에까지 배지가 붙어 채점자가 헛걸음했다.
 *
 * 모르는 것을 말하는 표시는 없는 편이 낫다.
 *
 * **「달라졌는가」를 백엔드에 요청하지 않기로 했다**(오너 판단 2026-09-06). 요청서를 썼다가 접었고,
 * 접은 근거를 여기 남긴다 — 같은 생각이 다시 올라오면 이 문단을 먼저 읽으면 된다:
 *   ① **채점 기준은 배정에 굳은 정답이다.** 학생은 출제 당시 문제를 풀었으니 그 시점 정답으로
 *      매기는 것이 옳다. 지금 문제의 정답이 달라졌더라도 채점자가 할 일은 바뀌지 않는다 —
 *      알아도 행동이 없는 정보다.
 *   ② 2026-09-06 이후 배포는 전부 'snapshot' 이라 값이 이미 그 시점으로 굳는다. 애초에 물을
 *      일이 없다.
 *   ③ 문제가 되는 'current' 는 그 전 배정뿐인데, 회신 §4-1 이 「배정 자체가 거의 없어 실질
 *      영향 없음」이라 했다.
 * 정답이 실제로 잘못돼 정정한 경우는 별개 사건이다 — 배지 하나로 다룰 일이 아니라 그 배정을
 * 다시 내거나 손으로 바로잡을 일이다.
 * normalizeItems 는 answer_source 를 계속 싣는다 — 화면에 그리지 않을 뿐, 응답이 주는 사실이다.
 */
function AnswerCell({ answer }) {
  const shown = answer ? parseAnswerForDisplay(answer) : { mode: 'empty', value: '' };
  if (shown.mode === 'empty') {
    return (
      <span className="text-xs text-gray-400" title="문제은행에 이 문항의 정답이 없습니다">
        정답 정보 없음
      </span>
    );
  }
  return (
    <span className="inline-block rounded-lg bg-blue-50 px-3 py-1.5 text-sm">
      {shown.mode === 'math' ? (
        // 렌더에 실패해도 값은 보여 준다 — 못 그리는 수식이라고 정답을 감출 이유가 없다
        <InlineMath
          math={shown.value}
          renderError={() => <code className="rounded bg-gray-100 px-1 text-[0.9em] text-gray-700">{answer}</code>}
        />
      ) : (
        // 원문자(①~⑤)는 같은 크기여도 글리프 잉크가 낮아 작아 보인다 — 이 칸만 20px 로 올린다
        <span className="text-[20px] font-medium leading-none text-gray-900">{shown.value}</span>
      )}
    </span>
  );
}

// 인쇄 미리보기 — 시험지 편집 창(PaperComposeModal)과 **같은 최신 인쇄 모달**(v4, A4·B4 용지 선택)이다.
// katex·조판 코드를 통째로 끌어오므로 누를 때만 불러온다(그 창과 같은 lazy).
const PrintPreviewModal = lazy(() => import('../printPreview').then((m) => ({ default: m.PrintPreviewModal })));

/*
 * headerSlot      모달이 머리에 비워 둔 칸(닫기 X 바로 왼쪽). 있으면 [새로고침]을 그 칸에 포털로 그린다.
 *                 없으면(모달 밖·시험) 종전처럼 카드 머리에 둔다 — 새로고침이 사라지는 화면이 없게.
 * onOverlayChange 인쇄 미리보기가 떠 있는지 모달에 알린다. 모달은 그동안 inactive 로 Esc 를 내려놓는다.
 */
export default function AssignmentPanel({
  assignmentId, onBack, onChanged, onDirtyChange = null, embedded = false,
  headerSlot = null, onOverlayChange = null,
}) {
  const { getAccessToken, userType } = useAuth();
  /**
   * 배정 삭제(`DELETE /exam-papers/assignments/{aid}`)는 서버가 교사에게 403 으로 남겼다
   * (BE-20260910 회신 §1-2·§5-③ — 채점 기록까지 지우는 비가역 삭제).
   * 배정 **만들기·채점·마감·재오픈은 교사에게 열려 있다** — 지우는 것 하나만 다르므로
   * 이 판정을 그 버튼들에 함께 걸지 않는다.
   */
  const canDeleteAssignment = ASSIGN_DELETE_ROLES.includes(userType);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  /**
   * [새로고침](↻)이 도는 중 — **loading 과 다른 상태다.**
   * loading 은 화면을 스켈레톤으로 갈아치우는 「처음 여는 조회」의 것이고, 이쪽은 서 있는 화면을
   * 그대로 둔 채 값만 갈아 끼우는 조회의 것이다. 한 상태로 겸하면 새로고침마다 본문이 통째로
   * 사라졌다가 돌아와, 내용 높이로 서는 창(AssignmentGradeModal → Modal 은 height:auto)이
   * 눌렸다 부푼다(오너 지적 2026-09-07).
   */
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(null); // 'save' | 'close' | 'reopen' | 'delete'
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(false);
  const [confirmReopen, setConfirmReopen] = useState(false);
  // 테스트 삭제(2026-09-06 · dev 537792e) — 배정 행과 그 채점 기록을 함께 지운다. 되돌릴 수 없다.
  const [confirmDelete, setConfirmDelete] = useState(false);
  // 서버가 "예약이라 안 된다"(409 scheduled)고 답한 뒤부터는 화면도 잠근다. 상세를 다시 읽어 잠그지 않는
  // 이유는 쌓아 둔 채점이 서버 값으로 덮여 사라지기 때문이다 — 값은 그대로 두고 이 플래그만 세운다(§1-2).
  const [scheduledLock, setScheduledLock] = useState(false);
  const [draft, setDraft] = useState({}); // 화면에서 만진 값
  const [saved, setSaved] = useState({}); // 서버가 알고 있는 값 — 미저장 판정의 기준

  const seqRef = useRef(0);
  // 로딩 표시는 화면을 여는 조회의 것이다 — 무효화 카운터를 같이 쓰면 폴백 조회(save·close 뒤의
  // silent load)가 번호를 올리는 순간 스켈레톤이 영원히 남는다.
  const uiSeqRef = useRef(0);
  const aliveRef = useRef(true);
  const busyRef = useRef(false); // 이중 제출 차단
  const changedRef = useRef(onChanged);
  changedRef.current = onChanged;
  // StrictMode 는 마운트 → cleanup → 마운트를 돌린다 — 마운트마다 세우지 않으면 dev 에서 false 로 굳는다
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const items = useMemo(() => normalizeItems(detail), [detail]);

  /**
   * 인쇄 미리보기 — 값이 있으면 열려 있다. 넘기는 것은 **배포본의 문항 순서** 그대로의 problem_id 다
   * (시험지를 다시 읽지 않는다 — 학생이 받은 것이 곧 이 목록이다). 문항 본문은 인쇄 모달이 은행에서 읽는다.
   * preset_id 는 넣지 않는다 — 넣으면 레거시 프리셋 인쇄 경로로 빠진다(PaperComposeModal 과 같은 주의).
   * 값을 열 때 한 번 굳히는 이유도 같다: testData 가 매 렌더 새 객체면 미리보기가 계속 다시 읽는다.
   */
  const [printData, setPrintData] = useState(null);
  const printable = useMemo(() => items.filter((it) => it.problemId != null), [items]);
  const onOverlayRef = useRef(onOverlayChange);
  onOverlayRef.current = onOverlayChange;
  useEffect(() => { onOverlayRef.current?.(printData != null); }, [printData]);
  // 인쇄창을 띄운 채 이 패널이 사라져도 모달이 inactive 로 굳지 않게
  useEffect(() => () => { onOverlayRef.current?.(false); }, []);
  /*
   * 인쇄 머리(PrintSheetV4)에 들어가는 값 — 이 창은 **한 학생에게 나간 배포본**이라 머리가 그 사실을 적는다
   * (오너 지시 2026-09-10: 「학생이름이 적혀있는(출제된) 시험지 미리보기는 학생이름과 TID 가 입력되어야 함」).
   *   student_name → 「이름」 칸       exam_id → 「TID.」 칸(배정 번호 — 이 창 통계 줄의 「TID」와 같은 값)
   *   preset_name  → 제목 아래 회색 부제(시험지명 — 시험지 편집 창 미리보기와 같은 자리)
   * exam_type 은 넣지 않는다 — 배정의 값은 판별용 'paper_v2' 라, 넣으면 바닥 줄에 「카이테스트 · paper_v2」가 찍힌다.
   */
  const openPrint = () => {
    if (printable.length === 0) return;
    setPrintData({
      printContext: detail?.paper_id != null
        ? { kind: 'paper', id: String(detail.paper_id) }
        : { kind: 'assignment', id: String(assignmentId) },
      problems_list: printable.map((it) => ({ problem_id: Number(it.problemId) })),
      preset_name: detail?.paper_title || '',
      student_name: detail?.student_name || '',
      exam_id: assignmentId != null ? String(assignmentId) : '',
    });
  };

  /**
   * 본문 미리보기 — 문항 구성이 바뀌면 관찰을 다시 건다(지켜볼 `<tr>` 이 달라진다).
   * 자국이 있는 pid 는 훅이 걸러 다시 묻지 않으므로, 다시 걸려도 왕복이 늘지 않는다.
   */
  const rowsKey = useMemo(() => items.map((it) => it.problemId ?? '').join(','), [items]);
  const { listRef, htmlOf, isComplete, isFailed, retryFailed } = useRowBodies(getAccessToken, rowsKey);
  /**
   * 못 받은 **이 배정의** 문항 수. 훅이 주는 failedCount 를 그대로 쓰지 않는 이유:
   * 그 목록은 훅 인스턴스에 쌓이는데, 전체 화면 경로(`/exam-papers/assignment/:aid`)에서 주소만
   * 갈아 다른 배정으로 옮기면 이 패널은 **재마운트되지 않는다**(assignmentId prop 만 바뀐다).
   * 그때 앞 배정에서 실패한 건수가 새 배정 화면에 그대로 떠, 지금 목록과 무관한 숫자를 말하게 된다.
   * 지금 서 있는 문항만 세면 그 어긋남이 생길 자리가 없다.
   */
  const failedRowCount = useMemo(
    () => items.filter((it) => it.problemId != null && isFailed(it.problemId)).length,
    [items, isFailed],
  );

  const seed = useCallback((d) => {
    const map = {};
    normalizeItems(d).forEach((it) => { map[it.key] = it.result; });
    setSaved(map);
    setDraft(map);
  }, []);

  /**
   * 조회는 세 갈래고, **화면을 얼마나 건드리는가**가 갈리는 전부다.
   *   기본        처음 여는 조회 — 스켈레톤을 세우고, 실패하면 화면을 에러로 바꾼다
   *   silent      저장·마감·삭제 직후의 폴백 조회 — 아무것도 세우지 않는다(조작은 이미 성공했다)
   *   refresh     [새로고침](↻) — **서 있는 화면은 그대로 두고** 값만 갈아 끼운다. 도는 표시는 ↻ 하나뿐
   *
   * 새로고침이 기본 갈래를 쓰면 눌린 값이 사라졌다 돌아오면서 창 높이가 출렁인다 — 값만 바뀌면
   * 되는 자리에서 화면이 무너질 이유가 없다.
   */
  const load = useCallback(async ({ silent = false, refresh = false } = {}) => {
    if (assignmentId == null) return;
    const seq = (seqRef.current += 1);
    // 스켈레톤으로 갈아치우는 것도, 실패했을 때 화면을 비우는 것도 「처음 여는 조회」의 몫이다
    const quiet = silent || refresh;
    if (!quiet) { uiSeqRef.current = seq; setLoading(true); setError(null); }
    if (refresh) setRefreshing(true);
    try {
      const d = await fetchAssignment(assignmentId, getAccessToken);
      if (!aliveRef.current || seq !== seqRef.current) return; // 늦게 온 응답이 최신 상태를 덮지 않게
      setDetail(d);
      // 읽는 데 성공했으면 앞서 띄운 실패 화면을 남길 이유가 없다 — 새로고침으로 에러에서 회복된다
      setError(null);
      // 방금 읽은 상세가 진실이다 — 예약 잠금은 여기서 풀고, 필요하면 새 status 가 다시 잠근다
      setScheduledLock(false);
      seed(d);
    } catch (e) {
      if (!aliveRef.current || seq !== seqRef.current) return;
      if (quiet) {
        // 화면에 이미 서 있는 값이 있다 — 실패했다고 그것을 비우지 않는다. 말로만 알린다.
        setToast({ type: 'error', text: refresh
          ? '새로 읽지 못했습니다 — 화면은 조금 전 값 그대로입니다.'
          : '최신 상태를 읽지 못했습니다 — 새로고침해 주세요.' });
        return;
      }
      setError(describeApiError(e, '배포본을 불러오지 못했습니다.'));
      setDetail(null);
    } finally {
      if (aliveRef.current && !quiet && uiSeqRef.current === seq) setLoading(false);
      // 늦게 온 응답(seq 불일치)도 여기를 지난다 — 조건을 걸면 그때 ↻ 가 영영 도는 채로 남는다
      if (aliveRef.current && refresh) setRefreshing(false);
    }
  }, [assignmentId, getAccessToken, seed]);

  // 초기화는 배포본이 바뀔 때만 — load 의 정체성에 매달면 훗날 의존성 하나가 흔들리는 순간
  // 매 렌더가 채점 초안을 지우는 효과로 바뀐다.
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    setToast(null);
    setConfirmClose(false);
    setConfirmRevert(false);
    setConfirmReopen(false);
    setScheduledLock(false);
    setDraft({});
    setSaved({});
    if (assignmentId != null) loadRef.current();
  }, [assignmentId]);

  const closed = isClosedOf(detail);
  // 예약이면 채점·마감이 서버에서 막힌다(§1-2 A2·A3) — 화면도 같은 자리에서 막는다
  const scheduled = !closed && (isScheduledOf(detail) || scheduledLock);
  // 시각은 서버가 UTC로 준다(§1-1) — fmtServerDateTime이 한국 시각으로 표시한다
  const openAtText = detail?.open_at ? fmtServerDateTime(detail.open_at) : '';
  const closeAtText = detail?.close_at ? fmtServerDateTime(detail.close_at) : '';
  // 오픈 시각은 아직 오지 않았을 때만 뜻이 있다(형제 화면 AssignPanel 과 같은 규칙: status 50 && open_at)
  const openPendingText = scheduled ? openAtText : '';
  // 재오픈 뒤 돌아갈 상태를 미리 말하기 위한 값(마감 중에는 status 로 알 수 없다)
  const openAtPassed = openAtPassedOf(detail);
  // 배지는 화면의 판정과 어긋나면 안 된다 — 마감으로 다루면서 '진행 중'이 떠 있으면 화면이 거짓말을 한다
  const statusBadge = closed
    ? ASSIGN_STATUS[200]
    : (scheduled ? ASSIGN_STATUS[50] : assignStatusDisplay(detail));

  // 미저장 = 화면 값과 서버 값이 다른 문항. 저장 대상도 이 목록 그대로다(바뀐 것만 보낸다).
  const dirtyItems = useMemo(
    () => items.filter((it) => (draft[it.key] ?? null) !== (saved[it.key] ?? null)),
    [items, draft, saved],
  );
  // 부모(페이지)가 자리를 뜨기 전에 물을 수 있도록 알린다 — 이 패널이 사라지면 채점도 함께 사라진다
  const onDirtyRef = useRef(onDirtyChange);
  onDirtyRef.current = onDirtyChange;
  useEffect(() => { onDirtyRef.current?.(dirtyItems.length > 0); }, [dirtyItems.length]);
  useEffect(() => () => onDirtyRef.current?.(false), []);
  // 리포트에 빠지는 기준은 화면이 아니라 서버에 저장된 값이다
  const ungradedCount = items.filter((it) => (saved[it.key] ?? null) === null).length;
  // 삭제가 함께 지우는 채점 기록 수 — **서버에 저장된 것**을 센다. 화면에 쌓아 둔 미저장 값은 서버에 없다.
  const gradedCount = items.length - ungradedCount;

  const setGrade = (key, v) => {
    setDraft((prev) => ({ ...prev, [key]: v }));
    // 확인 바가 뜬 뒤 채점이 바뀌면 확인한 숫자와 어긋난다
    setConfirmClose(false);
    setConfirmRevert(false);
  };

  /**
   * 일괄 채점 — 38문항이면 38번 누르던 것을 한 번으로 줄인다(레거시 ResultInputModal:241 과 같은 동작).
   *
   * **보낼 수 있는 문항만 매긴다.** problem_id 가 없는 행은 저장 대상에서 빠지므로(save 의 filter),
   * 여기서 매기면 진행률만 부풀고 저장하면 그 문항은 그대로 남는다 — 화면이 거짓말을 한다.
   * 저장이 아니라 화면에 쌓기만 하는 것은 종전과 같다. [채점 저장]을 눌러야 서버에 남는다.
   */
  const setAllGrades = (v) => {
    setDraft((prev) => {
      const next = { ...prev };
      items.forEach((it) => { if (it.problemId != null) next[it.key] = v; });
      return next;
    });
    setConfirmClose(false);
    setConfirmRevert(false);
  };

  /**
   * 진행률 — **화면에 쌓인 값(draft)** 기준이다. 서버의 graded 를 쓰면 방금 매긴 것이 막대에 안
   * 보여, 매기는 사람이 자기 손을 확인할 수 없다.
   * 미채점(null)은 세지 않는다 — 셋이면 처음부터 막대가 가득 찬다.
   * 분모도 매길 수 있는 문항만이다(problem_id 없는 행은 영영 매길 수 없어 100%가 안 된다).
   */
  const progress = useMemo(() => {
    const gradable = items.filter((it) => it.problemId != null);
    const done = gradable.filter((it) => (draft[it.key] ?? null) !== null).length;
    return { done, total: gradable.length, pct: gradable.length > 0 ? (done / gradable.length) * 100 : 0 };
  }, [items, draft]);

  // 되돌리기는 쌓아 둔 채점을 통째로 버린다 — 마감과 같은 무게라 같은 인라인 확인을 거친다
  const runRevert = () => {
    setDraft(saved);
    setConfirmRevert(false);
    setToast({ type: 'info', text: '저장하지 않은 채점을 되돌렸습니다.' });
  };

  const save = async () => {
    // scheduled 는 서버가 409 로 막는 자리다(§1-2 A2) — [채점 저장] 버튼도 비활성이고 위에 안내 띠가 떠 있어
    // 여기까지 오는 길은 상태가 낡은 경합뿐이다. 그래서 조용히 되돌린다(closed 와 같은 취급).
    if (busyRef.current || dirtyItems.length === 0 || closed || scheduled) return false;
    busyRef.current = true;
    setBusy('save');
    setToast(null);
    const changed = dirtyItems
      .filter((it) => it.problemId != null)
      // 보내는 값은 **JSON boolean 또는 null 만**이다 — 1·0·'true' 가 섞이면 400 이다(§1-2 A2).
      // GradeControl 이 GRADE_OPTIONS 의 리터럴(true/false/null)만 돌려주므로 지금도 그 셋뿐이지만,
      // 세는 곳(dirtyItems)과 보내는 곳이 갈라지지 않게 보내기 직전에 같은 자로 한 번 더 고정한다.
      .map((it) => ({ key: it.key, problemId: it.problemId, result: toResult(draft[it.key]) }));
    if (changed.length === 0) {
      busyRef.current = false;
      setBusy(null);
      setToast({ type: 'error', text: '저장할 수 있는 문항이 없습니다 — 응답에 problem_id 가 없습니다.' });
      return false;
    }
    try {
      // 바뀐 문항만 보낸다 — 부분 채점이 계약이라 전건을 다시 밀어 넣을 이유가 없다
      const d = await patchResults(assignmentId, changed.map((c) => ({ problem_id: c.problemId, result: c.result })), getAccessToken);
      // 401 이면 쓰기 헬퍼가 /signin 으로 보내며 표식을 돌려준다 — 저장했다고 말하면 거짓말이 된다
      if (d === AUTH_REDIRECT) return false;
      if (!aliveRef.current) return true;
      if (Array.isArray(d?.items)) {
        // 응답이 갱신된 상세 전체다(§2-9) — 재-GET 없이 이것으로 화면을 갱신한다
        setDetail(d);
        seed(d);
      } else {
        // 상세 형태가 아니면 보낸 값만 기준으로 올린다 — 화면에서 "저장됨"이 유지된다
        setSaved((prev) => {
          const next = { ...prev };
          changed.forEach((c) => { next[c.key] = c.result; });
          return next;
        });
      }
      setToast({ type: 'info', text: `${changed.length}건을 저장했습니다.` });
      changedRef.current?.();
      return true;
    } catch (e) {
      if (!aliveRef.current) return false;
      // 409 reason:'scheduled' = 오픈 전이라 막혔다. 상세를 다시 읽어 잠그면 쌓아 둔 채점이 서버 값으로
      // 덮여 사라지므로, 값은 그대로 두고 화면만 잠근다(§1-2 A2).
      if (errorReason(e) === 'scheduled') setScheduledLock(true);
      setToast({ type: 'error', text: describeApiError(e, '채점을 저장하지 못했습니다.') });
      return false;
    } finally {
      busyRef.current = false;
      if (aliveRef.current) setBusy(null);
    }
  };

  const runClose = async () => {
    // 예약(오픈 전)이면 서버가 409 scheduled 로 막는다(§1-2 A3) — 버튼도 비활성이라 여기는 경합 방어다
    if (busyRef.current || scheduled) return;
    busyRef.current = true;
    setBusy('close');
    setToast(null);
    try {
      const d = await closeAssignment(assignmentId, getAccessToken);
      if (d === AUTH_REDIRECT || !aliveRef.current) return; // null = 401 로 /signin 으로 넘어간 길
      setConfirmClose(false);
      setToast({ type: 'info', text: '채점을 완료했습니다 — 채점한 문항이 KY Report에 반영됩니다.' });
      changedRef.current?.();
      await load({ silent: true });
    } catch (e) {
      if (!aliveRef.current) return;
      if (errorReason(e) === 'scheduled') setScheduledLock(true); // 오픈 전 — 채점과 같은 이유로 막힌다
      setToast({ type: 'error', text: describeApiError(e, '채점을 완료하지 못했습니다.') });
    } finally {
      busyRef.current = false;
      if (aliveRef.current) setBusy(null);
    }
  };

  /**
   * 테스트 삭제 — `DELETE /assignments/{aid}`(회신 `BE-20260906-reply-assignment-delete.md`).
   *
   * 배정 행은 soft delete 이고 그 배정의 **채점 기록은 함께 지워진다** — 그래야 학생의 개념별
   * 정오답에서도 빠진다(오너 결정). 응답 `deleted_records` 가 그 수라 지운 뒤 그대로 말한다.
   *
   * **마감분은 서버가 409 `closed` 로 막는다**(리포트에 편입된 상태라 재오픈이 안전장치다).
   * 화면도 버튼을 먼저 잠그지만, 다른 창에서 먼저 마감했으면 이 화면의 상태가 낡았을 수 있어
   * 보내기 직전에 한 번 더 본다 — 재오픈(runReopen)이 같은 자리에서 하는 것과 같은 방어다.
   *
   * 성공하면 **창을 닫고 목록을 다시 읽는다**(onChanged → onBack). 지운 배정을 다시 읽으면 404 라
   * 여기서 load 를 부르지 않는다.
   */
  const runDelete = async () => {
    if (busyRef.current) return;
    /**
     * 권한 갈래를 **마감 판정보다 앞에** 세운다. 순서가 뒤집히면 마감된 배정을 보는 교사가
     * 「[채점 수정]으로 마감을 먼저 풀어 주세요」를 받는다 — 풀어도 지울 수 없으니 거짓 안내다.
     * 마감은 같은 사람이 풀 수 있는 상태이고, 권한은 그 사람이 풀 수 없다. 그 둘을 섞지 않는다.
     *
     * 손잡이와 확인 바가 이미 감춰져 있어 여기까지 오는 길은 없다. 그래도 두는 이유는 삭제를
     * 실제로 쏘는 자리가 여기라, 훗날 이 함수를 다른 자리에서 부르면 그 경로만 무방비가 되기
     * 때문이다(형제 갈래인 closed·!closed 방어와 같은 취급).
     */
    if (!canDeleteAssignment) {
      setConfirmDelete(false);
      setToast({ type: 'error', text: '배정 삭제는 관리자만 할 수 있습니다.' });
      return;
    }
    if (closed) {
      setConfirmDelete(false);
      setToast({ type: 'error', text: '채점이 완료된 배정이라 삭제할 수 없습니다 — [채점 수정]으로 마감을 먼저 풀어 주세요.' });
      return;
    }
    busyRef.current = true;
    setBusy('delete');
    setToast(null);
    try {
      const d = await deleteAssignment(assignmentId, getAccessToken);
      if (d === AUTH_REDIRECT || !aliveRef.current) return;
      setConfirmDelete(false);
      // 미저장 채점이 남아 있어도 이제 물을 것이 없다 — 배정 자체가 사라졌다.
      onDirtyRef.current?.(false);
      changedRef.current?.();
      onBack && onBack();
    } catch (e) {
      if (!aliveRef.current) return;
      setToast({ type: 'error', text: describeApiError(e, '테스트를 삭제하지 못했습니다.') });
    } finally {
      busyRef.current = false;
      if (aliveRef.current) setBusy(null);
    }
  };

  const runReopen = async () => {
    if (busyRef.current) return;
    // 재오픈은 **마감된 배정에만** 뜻이 있다 — 미마감에 부르면 423 이다(§1-2 A1). 버튼은 마감일 때만
    // 그려지지만, 다른 창에서 먼저 재오픈했으면 이 화면의 상태가 낡았을 수 있어 보내기 직전에 다시 본다.
    if (!closed) {
      setConfirmReopen(false);
      setToast({ type: 'error', text: '채점 완료된 배정만 다시 수정할 수 있습니다 — 이 배정은 이미 수정 가능한 상태입니다. 새로고침(↻)으로 최신 상태를 읽어 주세요.' });
      return;
    }
    busyRef.current = true;
    setBusy('reopen');
    setToast(null);
    try {
      const d = await reopenAssignment(assignmentId, getAccessToken);
      if (d === AUTH_REDIRECT || !aliveRef.current) return; // null = 401 로 /signin 으로 넘어간 길
      setConfirmReopen(false);
      // 자동 마감 예약(close_at)까지 풀렸다는 것을 말한다 — 예전에는 예약이 남아 60초 안에 다시
      // 마감됐고, 그래서 "다시 열렸습니다"가 곧 거짓이 됐다(§1-2 A1).
      setToast({
        type: 'info',
        text: '채점을 다시 수정할 수 있습니다 — 자동 종료 예약도 함께 풀렸습니다. 지금은 라이브 리포트에서 빠져 있고, 수정 후 다시 [채점 완료]해야 반영됩니다.',
      });
      changedRef.current?.();
      await load({ silent: true });
    } catch (e) {
      if (!aliveRef.current) return;
      const text = describeApiError(e, '채점을 다시 수정할 수 있도록 열지 못했습니다.');
      // 423 = 마감이 아니었다 → 이 화면의 상태가 낡았다는 뜻이다. 마감 화면에는 미저장 채점이 있을 수
      // 없으므로(채점 UI 자체가 안 뜬다) 여기서는 다시 읽어도 잃을 것이 없다.
      if (e?.httpStatus === 423) await load({ silent: true });
      // 문구는 재조회 뒤에 세운다 — silent 재조회가 실패하면 자기 문구로 덮어써 사유가 사라진다
      if (aliveRef.current) setToast({ type: 'error', text });
    } finally {
      busyRef.current = false;
      if (aliveRef.current) setBusy(null);
    }
  };

  // 미저장 채점은 이 패널이 사라지는 순간 사라진다 — 나가는 길과 덮어쓰는 길 모두에서 한 번 묻는다
  const confirmDiscard = (question) => {
    if (dirtyItems.length === 0) return true;
    // eslint-disable-next-line no-alert
    return window.confirm(`저장하지 않은 채점 ${dirtyItems.length}건이 있습니다. ${question}`);
  };

  const handleBack = () => {
    if (!confirmDiscard('버리고 나갈까요?')) return;
    // 여기서 내려야 부모의 가드가 같은 질문을 한 번 더 하지 않는다(이 클릭이 곧 언마운트다)
    onDirtyRef.current?.(false);
    onBack && onBack();
  };

  // 새로고침은 서버 값을 그대로 덮어쓴다 — 미저장 채점이 있을 때가 하필 이 버튼이 살아 있는 때다.
  // 덮어쓰는 것은 **값뿐**이다: 화면은 선 채로 두고 ↻ 만 돈다(load 의 refresh 갈래).
  const handleRefresh = () => {
    if (!confirmDiscard('새로고침하면 서버에 저장된 값으로 덮어써집니다. 버리고 새로 읽을까요?')) return;
    load({ refresh: true });
  };

  /**
   * 난이도는 서버가 100배 정수로 준다(316 = 3.16). 되돌리는 자를 새로 만들지 않는다 —
   * 이 저장소가 다섯 곳에서 같은 식을 쓴다(HubAssignmentCard · ReportModal:1906 ·
   * PresetTable:52 · Datapage:728 · useTests:135).
   *
   * ⚠ **배정 상세에 이 값이 온다는 보장이 없다.** 계약 §1-1 이 difficulty 를 실어 오는 것은
   * 배정 **목록**(GET /papers/{id}/assignments)이고, 상세(§2-9)의 필드 목록에는 없다.
   * 그래서 없으면 '-' 로 둔다 — 지어내지 않는다(오너 결정 2026-09-06 「있는 것만 그린다」).
   */
  const rawDifficulty = Number(detail?.difficulty);
  const difficultyText = Number.isFinite(rawDifficulty) && rawDifficulty > 0
    ? (rawDifficulty / 100).toFixed(2)
    : '-';

  /**
   * 상태 아이콘 — 학습 피드의 배정 카드(StudentsHub/HubAssignmentCard:statusIcon)와 같은 3갈래다.
   * 같은 클립보드가 **색으로만** 채점 여부를 말하고, 예약만 시계·보라로 따로 선다.
   * 그 파일에서 가져오지 않는 이유는 층 때문이다 — 시험지(ExamPapersV2)가 허브(StudentsHub)를
   * 끌어오면 의존이 뒤집힌다. 규칙이 갈리면 두 자리를 함께 고친다.
   */
  const headIcon = closed
    ? { Icon: ClipboardList, cls: 'text-primary' }
    : (scheduled ? { Icon: CalendarClock, cls: 'text-violet-500' } : { Icon: ClipboardList, cls: 'text-gray-400' });
  const HeadIcon = headIcon.Icon;

  /**
   * 머리줄의 얼굴은 **학습 피드의 배정 카드**(StudentsHub/HubAssignmentCard 의 compact 모양)를
   * 그대로 옮긴 것이다(오너 지시 2026-09-06). 같은 배포본을 가리키는 자리라 두 화면이 같은
   * 얼굴을 쓴다 — 피드에서 카드를 눌러 이 창이 열리므로, 열린 뒤에도 같은 카드가 서 있어야
   * 「무엇을 열었는지」가 이어진다.
   *
   * 카드와 갈리는 곳은 셋이고, 전부 **누르는 카드가 아니라서** 생긴 차이다:
   *   · hover 확대·그림자·초점 링·cursor-pointer 를 뺀다(피드 카드는 버튼이다).
   *   · 오른쪽 [>](ChevronRight) 자리에 [새로고침]을 놓는다 — 이 자리에서 갈 곳은 없고, 할 일은
   *     서버 값을 다시 읽는 것뿐이다.
   *   · 마감이어도 배지를 **글자로** 둔다. 카드는 마감을 초록 동그라미 하나로 말하지만, 채점 창은
   *     그 상태가 곧 채점·마감 버튼의 잠금 근거라 화면이 글자로 말해야 한다.
   *
   * 제목 자리에 시험지명이 아니라 **학생명**이 서는 이유: 카드에서 제목은 그 피드의 주인공이고,
   * 이 창의 주인공은 채점 대상인 학생이다. 같은 자리에 각 화면의 주인공을 놓은 것이다.
   */
  /**
   * [새로고침] — 모달 안이면 **닫기(X) 바로 왼쪽**(headerSlot), 모달 밖이면 카드 머리. 두 자리에서 모양이
   * 다른 이유: 모달 머리에서는 옆의 닫기(X)와 **같은 층**이라 같은 테두리·크기를 쓰고, 카드 머리에서는
   * 종전의 작은 회색 아이콘이다. 도는 것은 아이콘 하나뿐인 것은 두 자리가 같다.
   */
  const refreshBtn = (inModalHead) => (
    <button
      type="button"
      onClick={handleRefresh}
      disabled={loading || refreshing || busy != null}
      aria-label="배포본 새로고침"
      title="새로고침"
      className={inModalHead
        ? 'rounded-full border border-gray-300 p-1.5 text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-40'
        : 'rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40'}
    >
      {/* 도는 것은 이 아이콘 하나다 — 본문은 제자리에 선 채로 값만 바뀐다 */}
      <RefreshCw className={`${inModalHead ? 'size-5' : 'size-3.5'} ${loading || refreshing ? 'animate-spin' : ''}`} />
    </button>
  );

  const header = (
    <div className="flex shrink-0 items-center gap-2">
      {/* 모달은 자기 [닫기](X)가 있고 미저장 확인도 자기가 한다 — 여기까지 그리면 버튼이 둘이 된다 */}
      {!embedded && (
        <Button size="xs" variant="secondary" onClick={handleBack} icon={<ArrowLeft className="size-3.5" />}>뒤로</Button>
      )}
      <div className="min-w-0 flex-1 rounded-2xl border border-gray-200 bg-white p-2.5 shadow-sm">
        <div className="mb-1 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <HeadIcon size={20} className={`shrink-0 ${headIcon.cls}`} />
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <h3 className="truncate text-[12px] font-semibold">
                  {detail ? (detail.student_name || '(이름 없음)') : '배포본'}
                </h3>
                {detail && (
                  // 두 갈래(마감/진행 중)로는 예약을 말할 수 없다 — 형제 화면(AssignPanel)과 같은 3갈래 배지를 쓴다.
                  // 문구·색은 공용 상수 그대로다. 피드 카드는 100 을 주황 「미채점」으로 덮지만(할 일이 남았다는 뜻),
                  // 배포 창의 열림·닫힘을 말하는 이 자리에서는 초록 「진행 중」이 맞다(HubAssignmentCard 주석과 같은 판정).
                  <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${statusBadge.cls}`}>
                    {statusBadge.text}
                  </span>
                )}
              </div>
              {/* 부제 — 카드의 「시험구분 · 배정시각」 자리. 여기서는 시험지명과 그 배포본이 굳힌 판이다.
                  이 판은 **현재 시험지 판과 견주지 않는다** — 이 패널은 배정 번호 하나만 받고 시험지를
                  따로 읽지 않아, 견주려면 GET /papers/{id} 왕복이 하나 더 붙는다. 대조는 목록에서 한다. */}
              {detail && (
                <p
                  className="truncate text-[10px] text-gray-500"
                  title={detail.paper_revision != null ? '배포 시점 판 — 시험지를 고쳐도 이 배포본은 바뀌지 않습니다' : undefined}
                >
                  {detail.paper_title || '(제목 없음)'}
                  {detail.paper_revision != null ? ` · rev ${detail.paper_revision}` : ''}
                </p>
              )}
            </div>
            {/* 테스트 삭제 — **제목 묶음 바로 오른쪽**이다(오너 지시 2026-09-10 — 지우는 대상이 곧 이 제목의 배포본이라
                그 옆에 붙는다). 권한이 있는 사람에게만 **손잡이 자체를 그린다.** 마감분에서는 잠근다
                (서버 409 를 기다리지 않고 화면이 먼저 막는다). 잠긴 까닭과 그때 할 일을 title 로
                말한다 — 눌리지 않는 버튼만 남기지 않는다.

                ⚠ **권한을 `disabled` 에 더하지 않는다.** 이 버튼의 기존 잠금은 「마감」이고 그
                title 이 「먼저 [채점 수정]으로 마감을 풀어 주세요」라고 **할 일**을 말한다. 마감은
                같은 사람이 풀 수 있는 상태라 그 안내가 참이다. 권한은 그 사람이 풀 수 없으니,
                `|| !canDeleteAssignment` 를 더하면 교사에게 영영 회색인 죽은 버튼이 남고 title 은
                여전히 마감 문구를 말해 거짓 안내가 된다. 그래서 감춘다 — 기존 disabled 조건은
                한 글자도 건드리지 않았다. */}
            {canDeleteAssignment && (
              <button
                type="button"
                onClick={() => setConfirmDelete((v) => !v)}
                disabled={loading || refreshing || busy != null || !detail || closed}
                aria-label="테스트 삭제"
                title={closed
                  ? '채점이 완료된 배정입니다 — 먼저 [채점 수정]으로 마감을 풀어 주세요'
                  : '이 테스트를 삭제합니다 — 채점 결과도 함께 지워집니다'}
                className={`shrink-0 rounded-full p-1 transition-colors disabled:opacity-40 ${
                  confirmDelete ? 'bg-danger/10 text-danger' : 'text-gray-400 hover:bg-gray-100 hover:text-danger'}`}
              >
                <Trash2 className="size-3.5" />
              </button>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {/* 인쇄 미리보기 — 삭제·새로고침이 비운 자리. 이 배포본의 문항으로 최신 인쇄 모달(A4·B4)을 연다.
                문항이 하나도 없으면(problem_id 가 전부 비어 온 응답) 인쇄할 것이 없어 잠근다. */}
            <Button
              size="xs"
              variant="secondary"
              onClick={openPrint}
              disabled={loading || !detail || printable.length === 0}
              title="인쇄 미리보기 — A4·B4 용지를 골라 인쇄합니다"
            >
              <Printer aria-hidden="true" className="mr-1 inline size-3.5" />
              미리보기
            </Button>
            {!(embedded && headerSlot) && refreshBtn(false)}
          </div>
        </div>

        {/* 한 줄 통계 — 피드 카드와 같은 네 칸(TID·난이도·문항·채점).
            채점 칸만 값의 출처가 다르다: 카드는 서버가 준 graded 를 읽지만, 이 창은 **화면에 쌓인
            값**을 센다(아래 진행률 막대와 같은 자). 방금 매긴 O·X 가 안 세지면 화면이 거짓말을 한다. */}
        <div className="flex items-center justify-between whitespace-nowrap text-[10px] text-gray-500">
          <span>
            {/* 배정 번호를 부르는 이름은 TID 다(오너 2026-09-06) — 레거시 결과표가 시험 한 건을
                그렇게 부르고, v2 에서 그 한 건에 해당하는 것이 배정이다 */}
            <span className="text-gray-400">TID </span>
            <span className="font-semibold text-gray-800">{assignmentId ?? '-'}</span>
          </span>
          <span>
            <span className="text-gray-400">난이도 </span>
            <span className="font-semibold text-gray-800">{difficultyText}</span>
          </span>
          <span>
            <span className="text-gray-400">문항 </span>
            <span className="font-semibold text-gray-800">{items.length > 0 ? items.length : '-'}</span>
          </span>
          <span>
            <span className="text-gray-400">{closed ? '채점 완료 ' : '채점 '}</span>
            <span className="font-semibold text-primary">
              {progress.total > 0 ? `${progress.done}/${progress.total}` : '—'}
            </span>
          </span>
        </div>

        {/* 시각은 전부 서버가 'Z'(UTC)로 준다(§1-1) — 손으로 9시간을 더하지 않는다.
            자동 마감 칩은 재오픈이 close_at 을 정말 풀었는지 눈으로 확인하는 자리이기도 하다(§1-2 A1).
            피드 카드에는 없는 줄이다 — 배포 창의 열고 닫는 시각은 채점하는 자리에서만 쓸모가 있다. */}
        {detail && (openPendingText || closeAtText) && (
          <div className="mt-1 flex flex-wrap items-center gap-x-2 text-[10px] text-gray-400">
            {openPendingText && <span>오픈 예정 {openPendingText}</span>}
            {closeAtText && <span>자동 종료 {closeAtText}</span>}
          </div>
        )}
      </div>
    </div>
  );

  if (assignmentId == null) return null;

  // 뿌리는 여백만 잡는다 — 이 패널은 페이지의 흰 카드(rounded-3xl bg-white p-4 shadow-lg) 안에
  // 들어가므로, 같은 카드를 한 번 더 두르면 흰 바탕 위에 흰 카드가 겹치고 여백이 두 겹이 된다.
  /**
   * 창 모드의 뼈대 — **표만 스크롤하고 나머지는 제자리에 선다**(오너 지시 2026-09-06).
   * 창 본문 전체가 스크롤하면 진행률 막대가 표에 밀려 화면 밖으로 나가, 정작 어디까지 매겼는지를
   * 보려고 끝까지 내려가야 한다. 그래서 창에서는 이 패널이 세로 flex 로 서고 표가 flex-1 을 받는다.
   * `min-h-0` 이 핵심이다 — flex 항목의 기본 min-height 가 auto 라 이것이 없으면 표가 줄지 않고
   * 그대로 넘쳐 흐른다. 전체 화면에서는 페이지가 스크롤하므로 종전대로 둔다.
   */
  return (
    <div className={embedded ? 'flex min-h-0 flex-1 flex-col' : ''}>
      {embedded && headerSlot && createPortal(refreshBtn(true), headerSlot)}
      {printData && (
        <Suspense fallback={null}>
          <PrintPreviewModal
            isOpen
            onClose={() => setPrintData(null)}
            testData={printData}
            isMobileView={window.innerWidth < 768}
          />
        </Suspense>
      )}
      {header}

      {/* 라이브 리전은 항상 마운트해 두고 문구만 바꾼다 — 내용과 함께 삽입되면 낭독이 불안정하다 */}
      <p
        role={toast?.type === 'error' ? 'alert' : 'status'}
        aria-live={toast?.type === 'error' ? 'assertive' : 'polite'}
        className={`mt-1.5 text-xs ${toast ? (toast.type === 'error' ? 'text-danger' : 'text-gray-600') : 'sr-only'}`}
      >
        {toast?.text || ''}
      </p>

      {loading && (
        <div className="mt-3 space-y-1.5" aria-busy="true">
          {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-12 rounded-xl bg-gray-100 motion-safe:animate-pulse" />)}
        </div>
      )}

      {!loading && error && (
        <div role="alert" className="mt-3 space-y-2 rounded-xl border border-red-100 bg-red-50 p-3">
          <p className="text-sm text-red-700">{error}</p>
          <Button size="xs" variant="secondary" onClick={() => load()}>다시 시도</Button>
        </div>
      )}

      {!loading && !error && detail && (
        <>
          {/* 미저장 경고는 지우지 않는다 — 저장 없이 자리를 뜨면 채점이 사라진다 */}
          {dirtyItems.length > 0 && (
            <div className="mt-2 flex items-center gap-1.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <AlertTriangle aria-hidden="true" className="size-3.5 shrink-0" />
              저장하지 않은 채점 <b>{dirtyItems.length}건</b> — [채점 저장]을 눌러야 서버에 남습니다.
            </div>
          )}

          {/* 예약(오픈 전) 잠금 — 서버가 409 reason:'scheduled' 로 막는 자리다(§1-2 A2·A3).
              누를 수 있게 두면 눌러 본 뒤에야 안 된다는 걸 알게 된다. 막되, 왜 막혔는지 화면이 먼저 말한다. */}
          {scheduled && (
            <div className="mt-2 flex items-start gap-1.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
              <AlertTriangle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              <span>
                아직 <b>오픈 전(예약)</b>이라 채점하거나 완료할 수 없습니다
                {openPendingText ? <> — 오픈 시각 <b>{openPendingText}</b> 이후에 열립니다.</> : '.'}
                {' '}시각이 지났는데도 잠겨 있으면 새로고침(↻)을 누르세요.
              </span>
            </div>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
            <span>문항 {items.length}개</span>
            <span>미채점 {ungradedCount}개</span>
            <span className="ml-auto" />
            {!closed && (
              <>
                {/* 확인 바는 한 번에 하나만 — 두 개가 겹치면 어느 쪽을 확정하는지 흐려진다 */}
                <Button size="xs" variant="secondary" onClick={() => { setConfirmClose(false); setConfirmRevert(true); }} disabled={dirtyItems.length === 0 || busy != null || confirmRevert} icon={<Undo2 className="size-3" />}>
                  되돌리기
                </Button>
                {/* 예약이면 서버가 409 로 막는다(§1-2) — 위 안내 띠가 이유를 말하고, 버튼은 잠긴다 */}
                <Button size="xs" onClick={save} loading={busy === 'save'} disabled={dirtyItems.length === 0 || busy != null || scheduled} title={scheduled ? '아직 오픈 전(예약)이라 채점할 수 없습니다' : undefined} icon={<Save className="size-3" />}>
                  채점 저장
                </Button>
                <Button size="xs" variant="warning" onClick={() => { setConfirmRevert(false); setConfirmClose(true); }} disabled={busy != null || confirmClose || scheduled} title={scheduled ? '아직 오픈 전(예약)이라 채점을 완료할 수 없습니다' : undefined} icon={<Lock className="size-3" />}>
                  채점 완료
                </Button>
              </>
            )}
            {closed && (
              <Button size="xs" variant="secondary" onClick={() => setConfirmReopen(true)} disabled={busy != null || confirmReopen} icon={<Unlock className="size-3" />}>
                채점 수정
              </Button>
            )}
          </div>

          {/* 되돌리기 확인 — 한 번 누르면 쌓아 둔 채점이 전부 사라진다 */}
          {confirmRevert && !closed && dirtyItems.length > 0 && (
            <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs leading-relaxed text-amber-900">
                저장하지 않은 채점 <b>{dirtyItems.length}건</b>을 버리고 서버에 저장된 값으로 되돌립니다 — 버린 채점은 다시 매겨야 합니다.
              </p>
              <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                {/* 확인 바 네 곳(되돌리기·삭제·채점 수정·채점 완료)은 한 규칙이다 — 버튼은 **오른쪽 정렬**,
                    [취소]가 맨 왼쪽이고 **확인하는 실행 버튼이 맨 오른쪽**(공용 ConfirmModal 과 같은 순서,
                    오너 결정 2026-09-10). 경고 문장을 다 읽은 눈이 끝나는 자리에 실행이 선다. */}
                <Button size="sm" variant="secondary" onClick={() => setConfirmRevert(false)} disabled={busy != null}>취소</Button>
                {/* 저장이 막힌 동안(예약)에는 이 버튼도 잠근다 — 살려 두면 눌러도 아무 일이 없다(§1-2 A2) */}
                <Button size="sm" variant="secondary" onClick={save} loading={busy === 'save'} disabled={busy != null || scheduled} title={scheduled ? '아직 오픈 전(예약)이라 채점할 수 없습니다' : undefined} icon={<Save className="size-3.5" />}>
                  먼저 채점 저장
                </Button>
                {/* 되돌리기는 화면 안의 일이라 예약이어도 언제든 할 수 있다 */}
                <Button size="sm" variant="warning" onClick={runRevert} disabled={busy != null} icon={<Undo2 className="size-3.5" />}>
                  버리고 되돌리기
                </Button>
              </div>
            </div>
          )}

          {/* 삭제 확인 — 무엇이 함께 사라지는지 **수로** 말한 뒤에만 지운다(요청서 §3).
              마지막 줄이 §1-6 이다: 이미 저장·공유된 KY Report 스냅샷은 불변이라, 그 사실을 **지우기 전에**
              말해야 「지웠는데 리포트에 왜 남아 있냐」가 생기지 않는다.
              마감분에서는 바를 아예 내린다 — 버튼이 잠겨 있어 열 길이 없지만, 열려 있는 사이에 다른 창에서
              마감되면 눌러도 아무 일 없는 버튼이 된다(마감 확인 바와 같은 방어).
              권한 판정을 여기에도 and 로 더한다 — 손잡이가 없으면 이 바가 열릴 길이 없지만,
              **삭제를 실제로 쏘는 버튼이 이 안에 있어** 두 겹으로 막는다(감추는 자리 셋 중 둘째). */}
          {canDeleteAssignment && confirmDelete && !closed && (
            <div className="mt-2 rounded-xl border border-danger/30 bg-danger/5 p-3">
              <p className="text-xs leading-relaxed text-gray-800">
                이 테스트를 삭제하면 <b>채점 결과 {gradedCount}건</b>도 함께 지워지고, 학생의 개념별 정오답에서도 빠집니다.
                {' '}<b>되돌릴 수 없습니다.</b>
                <br />
                이미 저장해 공유한 KY Report에는 그대로 남습니다 — 삭제로 바뀌지 않습니다.
              </p>
              <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                <Button size="sm" variant="secondary" onClick={() => setConfirmDelete(false)} disabled={busy != null}>취소</Button>
                <Button size="sm" variant="danger" onClick={runDelete} loading={busy === 'delete'} disabled={busy != null} icon={<Trash2 className="size-3.5" />}>
                  삭제
                </Button>
              </div>
            </div>
          )}

          {/* 재오픈 확인 — 공백이던 "이미 반영된 결과는 어떻게 되나"가 contract-answers §3-3 으로 답이 왔다.
              라이브 집계에서는 즉시 빠지고, 이미 저장·공유된 스냅샷은 불변이며, 재마감하면 다시 편입된다.
              계약 §1-2 A1 로 자동 마감 예약(close_at)까지 함께 풀리는 것도 여기서 말한다. */}
          {confirmReopen && closed && (
            <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs leading-relaxed text-amber-900">
                채점 수정을 시작하면 결과를 다시 고칠 수 있고, 걸려 있던 <b>자동 종료 예약도 함께 풀립니다</b>.
                {' '}수정 중에는 이 배포본이 <b>라이브 리포트에서 빠져 있고</b>, 고친 결과는 <b>다시 [채점 완료]해야</b> 반영됩니다.
                {' '}이미 저장해 공유한 KY Report는 그대로 남습니다 — 채점 수정으로 바뀌지 않습니다.
                {detail?.open_at && !openAtPassed && (
                  <>
                    {' '}오픈 시각({openAtText})이 아직 오지 않아, 채점 수정을 시작하면 <b>예약</b> 상태로 돌아가 그때까지는 채점할 수 없습니다.
                  </>
                )}
              </p>
              <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                <Button size="sm" variant="secondary" onClick={() => setConfirmReopen(false)} disabled={busy != null}>취소</Button>
                <Button size="sm" variant="warning" onClick={runReopen} loading={busy === 'reopen'} disabled={busy != null} icon={<Unlock className="size-3.5" />}>
                  채점 수정 시작
                </Button>
              </div>
            </div>
          )}

          {/* 마감 확인 — 무엇이 리포트에서 빠지는지 숫자로 보여 준 뒤에만 닫는다.
              예약이면 마감이 409 로 막히므로(§1-2 A3) 바를 아예 내린다 — 열려 있는 사이에 잠금이
              걸리는 경합이 있어, 남겨 두면 [마감 확정]이 눌러도 아무 일 없는 버튼이 된다. */}
          {confirmClose && !closed && !scheduled && (
            <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs leading-relaxed text-amber-900">
                채점을 완료하면 <b>채점한 문항만</b> KY Report에 반영됩니다 — 지금 미채점 <b>{ungradedCount}개</b>는 리포트에서 빠집니다.
                {dirtyItems.length > 0 && (
                  <>
                    {' '}저장하지 않은 채점 <b>{dirtyItems.length}건</b>도 아직 서버에 없어 함께 빠집니다.
                  </>
                )}
              </p>
              <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                <Button size="sm" variant="secondary" onClick={() => setConfirmClose(false)} disabled={busy != null}>취소</Button>
                {dirtyItems.length > 0 && (
                  <Button size="sm" variant="secondary" onClick={save} loading={busy === 'save'} disabled={busy != null} icon={<Save className="size-3.5" />}>
                    먼저 채점 저장
                  </Button>
                )}
                <Button size="sm" variant="warning" onClick={runClose} loading={busy === 'close'} disabled={busy != null} icon={<Lock className="size-3.5" />}>
                  {dirtyItems.length > 0 ? '저장하지 않고 완료' : '채점 확정'}
                </Button>
              </div>
            </div>
          )}

          {/* 일괄 채점 — 38문항이면 38번 누르던 것을 한 번으로 줄인다.
              마감·예약에서는 내린다: 마감은 채점 컨트롤 자체가 없고, 예약은 저장이 409 로 막힌다(§1-2 A2).
              누르면 화면에만 쌓인다 — 서버로는 [채점 저장]이 보낸다(아래 진행률이 그 사이를 말한다). */}
          {!closed && !scheduled && items.length > 0 && (
            <div className="mt-3 flex shrink-0 gap-2">
              <button
                type="button"
                onClick={() => setAllGrades(true)}
                disabled={busy != null}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700 transition-colors hover:bg-green-100 disabled:opacity-40"
              >
                <CircleCheckBig aria-hidden="true" className="size-3.5" />
                전체 O
              </button>
              <button
                type="button"
                onClick={() => setAllGrades(false)}
                disabled={busy != null}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:opacity-40"
              >
                <CircleX aria-hidden="true" className="size-3.5" />
                전체 X
              </button>
            </div>
          )}

          {/* 본문을 못 받은 문항이 있으면 되돌릴 길을 남긴다 — 행마다 「불러오지 못했습니다」만
              남기면 다시 물을 방법이 화면에 없다. 얼굴은 형제 화면(StudentsHub/
              PaperProblemPreviewList)의 같은 배너 그대로다. 채점 자체는 이것과 무관하게 굴러간다 —
              본문은 「어느 문제인가」를 알아보는 보조이지 채점의 재료가 아니다.
              문항이 없으면 요청도 없어 이 띠가 뜰 일이 없다(아래 빈 목록 안내와 겹치지 않는다). */}
          {failedRowCount > 0 && (
            <div
              role="status"
              aria-live="polite"
              className="mt-2 flex shrink-0 items-center gap-2 rounded-xl bg-danger/5 px-2.5 py-2 text-[11px] text-danger"
            >
              <AlertTriangle className="size-3.5 shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1">{`문제 미리보기 ${failedRowCount}개를 불러오지 못했습니다.`}</span>
              <Button size="xs" variant="secondary" onClick={retryFailed}>다시 시도</Button>
            </div>
          )}

          {items.length === 0 ? (
            <p className="mt-3 rounded-xl border border-gray-100 p-6 text-center text-sm text-gray-400">
              스냅샷에 문항이 없습니다 — 빈 시험지가 배포된 것으로 보입니다.
            </p>
          ) : (
            /* 표로 그린다 — 문항이 세로로 정렬되면 「어디까지 매겼나」가 한눈에 보인다(레거시
               ResultInputModal:272 과 같은 얼굴). 좁은 폭에서는 표가 자기 안에서 가로로 스크롤한다.
               레거시에 없던 두 열(문제 번호 링크·본문 요약)은 v2 에서 유지한다 — 채점하다 문제를
               열어 볼 수 있는 길이고, 요약이 없으면 몇 번 문항인지 번호로만 짚어야 한다. */
            <div className={`mt-2 rounded-xl border ${embedded ? 'min-h-0 flex-1 overflow-auto' : 'overflow-x-auto'}`}>
              {/* 최소 폭 824px — 본문 칸의 하한(288)에 나머지 네 칸의 고정 폭(56+96+176+208=536)을
                  더한 값이다. 종전 640 은 평문 두 줄이던 시절의 값이라, 그 폭에서는 본문 칸이
                  104px 로 남아 조판 본문의 배율이 0.25(글자 4.4px)까지 떨어진다. */}
              <table className="w-full min-w-[824px] text-sm">
                {/* 머리줄은 스크롤에도 남는다 — 38행을 내려가는 동안 어느 칸이 채점인지 잃지 않는다.
                    전체 화면에서는 이 상자가 스크롤하지 않아 sticky 가 걸릴 일이 없다(무해). */}
                <thead className="sticky top-0 z-10 bg-gray-50">
                  <tr>
                    <th scope="col" className="w-14 whitespace-nowrap px-3 py-2 text-center text-xs font-medium tracking-wider text-gray-500">번호</th>
                    <th scope="col" className="w-24 whitespace-nowrap px-3 py-2 text-center text-xs font-medium tracking-wider text-gray-500">문제</th>
                    {/* 본문 칸의 폭에는 **바닥이 있다** — 문제 v2 목록 표와 같은 값·같은 근거다.
                        배율이 `min(0.67, 칸너비/423)` 이라 칸이 283.41px(=423×0.67) 아래로 내려가는
                        순간 뒤쪽 항이 이겨 글자까지 함께 작아진다. 그 위 첫 격자값이 min-w-72(288px). */}
                    <th scope="col" className="min-w-72 whitespace-nowrap px-3 py-2 text-left text-xs font-medium tracking-wider text-gray-500">본문 요약</th>
                    <th scope="col" className="w-44 whitespace-nowrap px-3 py-2 text-center text-xs font-medium tracking-wider text-gray-500">채점</th>
                    <th scope="col" className="w-52 whitespace-nowrap px-3 py-2 text-center text-xs font-medium tracking-wider text-gray-500">정답</th>
                  </tr>
                </thead>
                {/* ref 는 본문 훅의 관찰 컨테이너다 — 이 안의 [data-body-pid] 를 지켜보다가 화면에
                    든 것만 20개씩 받아 온다. `<table>` 이 아니라 `<tbody>` 에 거는 이유는 훅이
                    여기서부터 부모를 훑어 스크롤을 맡은 칸(아래 overflow-auto div)을 찾기 때문이다 —
                    스크롤러 자신에 걸면 관찰 기준이 그보다 바깥이 되어 안 보이는 행까지 미리 받는다. */}
                <tbody ref={listRef} className="bg-white">
                  {items.map((it) => {
                    const v = draft[it.key] ?? null;
                    const badge = resultDisplay(saved[it.key] ?? null);
                    return (
                      <tr
                        key={it.key}
                        /* 관찰 표식 — problem_id 가 없는 행은 물을 것이 없어 붙이지 않는다 */
                        data-body-pid={it.problemId == null ? undefined : String(it.problemId)}
                        className="border-b border-gray-100 transition-colors duration-150 last:border-b-0 hover:bg-primary/10"
                      >
                        <td className="px-3 py-2 text-center">
                          <span className="inline-flex size-7 items-center justify-center rounded-full bg-gray-100 text-xs font-medium text-gray-700">
                            {it.displayNo}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          {/* 번호 — 정본 부품. 배포 스냅샷에는 need_* 가 없어 색은 먹색으로 굳는다.
                              problem_id 가 비어 온 행은 갈 곳이 없다(아래 [채점 불가]와 같은 사정) */}
                          {it.problemId == null
                            ? <span className="font-mono text-xs text-gray-300">—</span>
                            : <ProblemIdLink id={it.problemId} />}
                        </td>
                        {/* 본문 — 지면과 같은 렌더러가 만든 HTML 이라 수식·그림이 그대로 나온다.
                            h-px 는 이 셀에 **명시적 높이**를 주기 위한 것이다: 표 셀의 height 는
                            최소값으로 읽혀 실제로는 행 높이가 내려오고, 안쪽 본문이 h-full 로 그
                            높이를 받아 채운다(fill). 이 한 줄이 없으면 h-full 이 기준을 못 찾는다. */}
                        <td className="h-px px-3 py-2">
                          <ProblemBodyPreview
                            compact
                            fill
                            maxScale={LIST_BODY_MAX_SCALE}
                            html={it.problemId == null ? null : htmlOf(it.problemId)}
                            /* 폴백은 **세 상태를 가른다.** 본문은 별도 왕복으로 뒤늦게 오므로
                               「아직 안 왔다」·「받았는데 없다」·「못 받았다」가 한 문구로 뭉치면
                               화면이 거짓말을 한다. 평문 요약이 있으면 그것이 먼저다 —
                               기다리는 동안에도 어느 문제인지 읽을 수 있다. */
                            fallback={it.preview || (
                              <span className="text-gray-300">
                                {it.problemId == null
                                  ? '문제 정보를 확인할 수 없습니다.'
                                  : isFailed(it.problemId)
                                  ? '미리보기를 불러오지 못했습니다.'
                                  : isComplete(it.problemId)
                                  ? '본문 미리보기 없음'
                                  : '문제 본문을 불러오는 중…'}
                              </span>
                            )}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex justify-center">
                            {closed ? (
                              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${badge.cls}`}>{badge.text}</span>
                            ) : it.problemId == null ? (
                              // 보낼 대상이 없다 — 누를 수 있게 두면 채점한 줄 알고 넘어간다
                              <span
                                className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] text-amber-700"
                                title="응답에 problem_id 가 없어 이 문항은 채점을 보낼 수 없습니다"
                              >
                                채점 불가
                              </span>
                            ) : (
                              <GradeControl
                                label={`${it.displayNo}번 문항 채점`}
                                value={v}
                                onChange={(next) => setGrade(it.key, next)}
                                // 예약이면 저장이 409 로 막힌다(§1-2 A2) — 매길 수 있게 두면 매긴 뒤에야 알게 된다
                                disabled={busy != null || scheduled}
                              />
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <AnswerCell answer={it.answer} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* 진행률 — 화면에 쌓인 값 기준이다. 서버의 graded 를 쓰면 방금 매긴 것이 막대에 안 보인다.
              마감된 배정에는 그리지 않는다 — 매길 것이 없어 늘 가득 찬 막대가 뜬다. */}
          {!closed && items.length > 0 && progress.total > 0 && (
            <div className="mt-3 shrink-0">
              <div className="mb-1 flex items-center justify-between text-xs text-gray-600">
                <span>진행률</span>
                <span className="font-medium">
                  {progress.done} / {progress.total}
                  {progress.done === progress.total && (
                    /* 이 「채점 완료」는 **화면에서 전 문항을 매겼다**는 뜻이다 — 위 [채점 완료]
                       버튼이 하는 마감(status 200)과는 다른 일이라, 여기 뜬 뒤에도 저장과 마감이
                       남아 있다. 같은 말이 한 화면에서 두 자리에 서지만, 이 줄은 진행률 옆이라
                       「몇 개까지 했나」를 읽는 자리로 문맥이 갈린다(오너 지시 2026-09-06). */
                    <span className="ml-2 text-green-600">✓ 채점 완료</span>
                  )}
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                <div
                  className={`h-full transition-all duration-300 ease-out ${progress.done === progress.total ? 'bg-green-500' : 'bg-primary'}`}
                  style={{ width: `${progress.pct}%` }}
                />
              </div>
            </div>
          )}

          {closed && (
            <p className="mt-2 text-[11px] text-gray-400">
              채점이 완료된 배포본이라 결과를 고칠 수 없습니다 — 고치려면 [채점 수정]을 누르세요.
            </p>
          )}
        </>
      )}
    </div>
  );
}
